import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useContext } from "react";
import styled, { css } from "styled-components";

type Props = WidgetProps;

export interface OrderDetailsShippingNotesStyles {
    titleText?: TypographyProps;
    notesText?: TypographyProps;
    wrapper?: InjectableCss;
}

export const orderDetailsShippingNotesStyles: OrderDetailsShippingNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding: 20px;
            background-color: ${UCSColors.gray10};
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 20px 10px;
                        gap: 0;
                    `,
                    css`
                        gap: 0;
                    `,
                ]);
            }}
        `,
    },
    titleText: {
        variant: "h3",
        as: "h3",
        css: css`
            @media print {
                font-size: 14px;
            }
        `,
    },
    notesText: {
        size: 18,
        weight: 400,
        color: UCSColors.gray03,
        css: css`
            line-height: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 17px;
                        `,
                    ],
                    "max",
                );
            }}
            @media print {
                font-size: 12px;
            }
        `,
    },
};

const styles = orderDetailsShippingNotesStyles;

const Separator = styled.div`
    height: 1px;
    width: 100%;
    margin: 24px 0;
    background-color: ${UCSColors.gray07};
`;

const OrderDetailsShippingNotes = ({id}: Props) => {
    const { value: order } = useContext(OrderStateContext);
    
    if (!order?.properties.orderShippingNotes) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.titleText}>{translate("Order Shipping Notes")}</Typography>
            <Hidden above="sm">
                <Separator />
            </Hidden>
            <Typography {...styles.notesText}>{order.properties.orderShippingNotes}</Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsShippingNotes,
    definition: {
        group: "Order Details",
        displayName: "Order Details Shipping Notes",
        allowedContexts: ["OrderDetailsPage"],
    },
};

export default widgetModule;
